import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, 
  Volume2, 
  Activity, 
  FileText, 
  Check, 
  User, 
  Calendar,
  Clock,
  Brain,
  Stethoscope,
  Pill,
  AlertTriangle,
  Save
} from 'lucide-react';

interface NoteAxisDemoProps {
  isActive: boolean;
}

const NoteAxisDemo: React.FC<NoteAxisDemoProps> = ({ isActive }) => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const [transcriptionText, setTranscriptionText] = useState('');
  const [audioWaveform, setAudioWaveform] = useState<number[]>([]);

  const phases = [
    { 
      name: 'listening', 
      icon: Volume2, 
      title: 'Listening...', 
      color: 'text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20',
      borderColor: 'border-red-200 dark:border-red-700'
    },
    { 
      name: 'processing', 
      icon: Activity, 
      title: 'Processing Audio...', 
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
      borderColor: 'border-yellow-200 dark:border-yellow-700'
    },
    { 
      name: 'transcribing', 
      icon: FileText, 
      title: 'Transcribing...', 
      color: 'text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-200 dark:border-blue-700'
    },
    { 
      name: 'analyzing', 
      icon: Brain, 
      title: 'AI Analysis...', 
      color: 'text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-200 dark:border-purple-700'
    },
    { 
      name: 'complete', 
      icon: Check, 
      title: 'Complete!', 
      color: 'text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-200 dark:border-green-700'
    }
  ];

  const sampleText = "Patient presents with mild fever and sore throat. Symptoms began 2 days ago. Temperature 101.2°F. Throat appears red and swollen. No other significant symptoms reported.";
  
  const analysisResults = {
    symptoms: ['Fever', 'Sore throat', 'Throat inflammation'],
    recommendations: ['Rest and fluids', 'Monitor temperature', 'Follow up in 3 days'],
    icd10: 'J02.9 - Acute pharyngitis, unspecified',
    confidence: 94
  };

  useEffect(() => {
    if (isActive) {
      const phaseInterval = setInterval(() => {
        setCurrentPhase(prev => (prev + 1) % phases.length);
        setTranscriptionText('');
      }, 3000);

      return () => clearInterval(phaseInterval);
    }
  }, [isActive]);

  // Generate audio waveform animation
  useEffect(() => {
    if (isActive && currentPhase === 0) {
      const waveInterval = setInterval(() => {
        setAudioWaveform(prev => {
          const newWave = Array.from({ length: 20 }, () => Math.random() * 100);
          return newWave;
        });
      }, 100);

      return () => clearInterval(waveInterval);
    }
  }, [isActive, currentPhase]);

  // Typing effect for transcription
  useEffect(() => {
    if (currentPhase === 2) {
      let index = 0;
      const typingInterval = setInterval(() => {
        if (index < sampleText.length) {
          setTranscriptionText(sampleText.slice(0, index + 1));
          index++;
        } else {
          clearInterval(typingInterval);
        }
      }, 50);

      return () => clearInterval(typingInterval);
    }
  }, [currentPhase]);

  const renderAudioWaveform = () => (
    <div className="flex items-center justify-center gap-1 h-16">
      {audioWaveform.map((height, index) => (
        <motion.div
          key={index}
          className="w-1 bg-red-400 rounded-full"
          animate={{ height: `${Math.max(height * 0.5, 10)}%` }}
          transition={{ duration: 0.1, ease: "easeInOut" }}
        />
      ))}
    </div>
  );

  const renderProcessingAnimation = () => (
    <div className="flex justify-center items-center h-16">
      <div className="relative">
        <motion.div
          className="w-12 h-12 border-4 border-yellow-200 dark:border-yellow-600 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute top-2 left-2 w-8 h-8 border-4 border-t-yellow-400 border-r-transparent border-b-transparent border-l-transparent rounded-full"
          animate={{ rotate: -360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    </div>
  );

  const renderTranscriptionBox = () => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600 shadow-sm"
    >
      <div className="flex items-center gap-2 mb-2">
        <FileText className="w-4 h-4 text-blue-500" />
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Live Transcription</span>
      </div>
      <div className="text-xs text-slate-600 dark:text-slate-400 min-h-[2.5rem] leading-relaxed">
        {transcriptionText}
        {currentPhase === 2 && (
          <motion.span
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.5, repeat: Infinity }}
            className="inline-block w-0.5 h-3 bg-blue-400 ml-1"
          />
        )}
      </div>
    </motion.div>
  );

  const renderAIAnalysis = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-3"
    >
      {/* Symptoms Detected */}
      <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <Stethoscope className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Symptoms Detected</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {analysisResults.symptoms.map((symptom, index) => (
            <motion.span
              key={symptom}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.2 }}
              className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-full text-xs"
            >
              {symptom}
            </motion.span>
          ))}
        </div>
      </div>

      {/* ICD-10 Code */}
      <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div className="flex items-center gap-2 mb-1">
          <Brain className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Suggested Diagnosis</span>
          <span className="ml-auto text-xs text-indigo-500 font-medium">{analysisResults.confidence}% confidence</span>
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400">{analysisResults.icd10}</div>
      </div>
    </motion.div>
  );

  const renderCompleteState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="space-y-3"
    >
      {/* Patient Summary */}
      <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-primary-500" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Patient Summary</span>
          <div className="ml-auto flex items-center gap-1">
            <Clock className="w-3 h-3 text-slate-400" />
            <span className="text-xs text-slate-500">2:34 PM</span>
          </div>
        </div>
        <div className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
          {sampleText}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-2">
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 p-2 bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 rounded-lg border border-primary-200 dark:border-primary-700"
        >
          <Save className="w-3 h-3" />
          <span className="text-xs font-medium">Save Note</span>
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg border border-green-200 dark:border-green-700"
        >
          <Pill className="w-3 h-3" />
          <span className="text-xs font-medium">Prescribe</span>
        </motion.button>
      </div>
    </motion.div>
  );

  const renderCurrentPhase = () => {
    switch (currentPhase) {
      case 0: return renderAudioWaveform();
      case 1: return renderProcessingAnimation();
      case 2: return renderTranscriptionBox();
      case 3: return renderAIAnalysis();
      case 4: return renderCompleteState();
      default: return renderAudioWaveform();
    }
  };

  const currentPhaseData = phases[currentPhase];

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full h-80 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-700 rounded-xl p-6 shadow-xl border border-slate-200 dark:border-slate-600"
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Mic className="w-6 h-6 text-primary-500" />
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">NoteAxis</h3>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${currentPhase < 4 ? 'bg-red-400 animate-pulse' : 'bg-green-400'}`} />
                <span className="text-xs text-slate-500">
                  {currentPhase < 4 ? 'Recording' : 'Ready'}
                </span>
              </div>
            </div>

            {/* Phase Indicator */}
            <motion.div
              key={currentPhase}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`p-3 rounded-lg mb-4 ${currentPhaseData.bgColor} border ${currentPhaseData.borderColor}`}
            >
              <div className="flex items-center gap-3">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  {React.createElement(currentPhaseData.icon, {
                    className: `w-5 h-5 ${currentPhaseData.color}`
                  })}
                </motion.div>
                <div>
                  <div className={`font-semibold text-sm ${currentPhaseData.color}`}>
                    {currentPhaseData.title}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    {currentPhase === 0 && 'Capturing audio input...'}
                    {currentPhase === 1 && 'Analyzing speech patterns...'}
                    {currentPhase === 2 && 'Converting speech to text...'}
                    {currentPhase === 3 && 'Extracting medical insights...'}
                    {currentPhase === 4 && 'Ready for review and action'}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Main Content */}
            <div className="flex-1">
              <motion.div
                key={currentPhase}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderCurrentPhase()}
              </motion.div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Progress</span>
                <span>{Math.round(((currentPhase + 1) / phases.length) * 100)}%</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-600 rounded-full h-1">
                <motion.div
                  className="bg-gradient-to-r from-primary-500 to-accent-500 h-1 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${((currentPhase + 1) / phases.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NoteAxisDemo;